import { Button } from "$components/button/button";
import { ConditionalDisplay } from "$components/conditional-display";
import { RadioGroupField as RHFRadioField } from "$components/rhf-fields";
import { toast } from "$global-providers/toast-provider";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Typography } from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import format from "date-fns/format";
import { PatientType } from "elephant-web/gql";
import React, { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { date, mixed, object, ref, string } from "yup";
import {
  useInvoicePatientDetailsQuery,
  useSetPatientTypeMutation,
} from "./__generated__/PatientTypeSection.gql";
import {
  ADMISSION_DATE,
  DISCHARGE_DATE,
  INPATIENT,
  INPATIENT_DETAILS,
  INPATIENT_NUMBER,
  OUTPATIENT,
  PATIENT_TYPE,
  WARD,
} from "./formConstants";
import type { InvoiceDetailsDispatcher } from "./hooks/useInvoiceDetailsDispatcher";
import { InpatientForm } from "./InpatientForm";

gql`
  mutation setPatientType($input: SetPatientTypeInput!) {
    setPatientType(input: $input) {
      _id
      patientType
      inpatientDetails {
        ward
        inpatientNumber
        admission
        discharge
      }
    }
  }
`;

gql`
  query invoicePatientDetails($invoiceId: String!) {
    invoice(invoiceId: $invoiceId) {
      _id
      patientType
      inpatientDetails {
        ward
        inpatientNumber
        admission
        discharge
      }
    }
  }
`;

function useValidationSchema() {
  const { formatMessage } = useIntl();
  return object({
    [PATIENT_TYPE]: mixed().oneOf([
      PatientType.Outpatient,
      PatientType.Inpatient,
    ]),
    [INPATIENT_DETAILS]: object().when(PATIENT_TYPE, {
      is: (patientType: string) => {
        return patientType === INPATIENT;
      },
      then: object({
        [WARD]: string().required(),
        [INPATIENT_NUMBER]: string()
          .matches(
            /^[A-Za-z0-9]+$/,
            formatMessage({
              id: "invoicing.inpatientNumber.input.error",
              defaultMessage:
                "Inpatient number can only contain alphanumeric characters",
            }),
          )
          .required(),
        [ADMISSION_DATE]: date().nullable(),
        [DISCHARGE_DATE]: date()
          .nullable()
          .min(
            ref(ADMISSION_DATE),
            formatMessage({
              id: "invoicing.admissionDate.input.error",
              defaultMessage: "Discharge date can not be before Admission date",
            }),
          ),
      }),
    }),
  });
}

type FormValues = {
  [PATIENT_TYPE]: PatientType;
  [INPATIENT_DETAILS]: {
    [WARD]?: string;
    [INPATIENT_NUMBER]?: string;
    [ADMISSION_DATE]?: string;
    [DISCHARGE_DATE]?: string;
  };
};

export const PatientTypeSection = ({
  invoiceId,
  editable,
  dispatcher,
  busy,
}: {
  invoiceId: string;
  editable: boolean;
  dispatcher: InvoiceDetailsDispatcher;
  busy?: boolean;
}): React.ReactElement => {
  const { formatMessage } = useIntl();
  const validationSchema = useValidationSchema();

  const { data: patientDetails } = useInvoicePatientDetailsQuery({
    variables: { invoiceId },
  });

  const patientTypeFormMethods = useForm<FormValues>({
    mode: "onTouched",

    resolver: yupResolver(validationSchema),
    defaultValues: {
      patientType: PatientType.Outpatient,
      inpatientDetails: null,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
  } = patientTypeFormMethods;

  const patient: PatientType = useWatch({
    name: PATIENT_TYPE,
    control,
  });
  const inpatientDetails = useWatch({
    name: INPATIENT_DETAILS,
    control,
  });

  useEffect(() => {
    if (patientDetails?.invoice) {
      reset(patientDetails.invoice, { keepValues: false });
    }
  }, [patientDetails?.invoice, reset]);

  const [updatePatientDetails, { loading }] = useSetPatientTypeMutation({
    onError() {
      toast.error(
        formatMessage({
          id: "invoicing.updatePatientInfo.error.msg",
          defaultMessage: "Failed to update the patient information",
        }),
      );
    },
    onCompleted() {
      toast.success(
        formatMessage({
          id: "invoicing.updatePatientInfo.success.msg",
          defaultMessage: "Patient details updated successfully",
        }),
      );
    },
  });

  const savePatientDetails = async (formValues: FormValues) => {
    const { patientType, inpatientDetails } = formValues;
    dispatcher.sendBusy();
    try {
      await updatePatientDetails({
        variables: {
          input: {
            _id: invoiceId,
            patientType: patientType,
            inpatientDetails:
              patientType === PatientType.Inpatient
                ? {
                    admission: inpatientDetails.admission
                      ? new Date(inpatientDetails.admission).toISOString()
                      : undefined,
                    discharge: inpatientDetails.discharge
                      ? new Date(inpatientDetails.discharge).toISOString()
                      : undefined,
                    inpatientNumber: inpatientDetails.inpatientNumber,
                    ward: inpatientDetails.ward,
                  }
                : null,
          },
        },
      });
    } finally {
      dispatcher.sendBusy(false);
    }
  };

  return (
    <Grid container>
      <ConditionalDisplay condition={editable}>
        <Grid item xs={12}>
          <ValidationContext.Provider value={{ validationSchema }}>
            <FormProvider {...patientTypeFormMethods}>
              <form
                name="PatientTypeForm"
                onSubmit={handleSubmit(savePatientDetails)}
              >
                <RHFRadioField
                  name={PATIENT_TYPE}
                  options={[
                    {
                      value: OUTPATIENT,
                      label: formatMessage({
                        id: "invoicing.patientType.radioInput.option.outpatient",
                        defaultMessage: "Outpatient",
                      }),
                      "data-testid": "invoicing.patientType.outpatient",
                    },
                    {
                      value: INPATIENT,
                      label: formatMessage({
                        id: "invoicing.patientType.radioInput.option.inpatient",
                        defaultMessage: "Inpatient",
                      }),
                      "data-testid": "invoicing.patientType.inpatient",
                    },
                  ]}
                  required
                />
                {patient === INPATIENT && <InpatientForm />}
                <Grid item xs={12}>
                  <Box pt={2} textAlign="right">
                    <Button
                      color="secondary"
                      type="submit"
                      disabled={!isDirty || !isValid || busy}
                      loading={loading}
                      startIcon={<SaveIcon />}
                      data-testid="invoicing.savePatientDetails.button"
                    >
                      <FormattedMessage
                        id="invoicing.invoice.patientDetails.save"
                        defaultMessage="Save patient details"
                      />
                    </Button>
                  </Box>
                </Grid>
              </form>
            </FormProvider>
          </ValidationContext.Provider>
        </Grid>
      </ConditionalDisplay>
      <ConditionalDisplay condition={!editable}>
        <Grid item xs={6}>
          <Typography variant="subtitle2">
            <b>{patient}</b>
          </Typography>
          <ConditionalDisplay condition={!!inpatientDetails?.ward}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="invoicing.patienType.ward"
                defaultMessage="Ward:"
              />
              {` ${inpatientDetails?.ward}`}
            </Typography>
          </ConditionalDisplay>
          <ConditionalDisplay condition={!!inpatientDetails?.inpatientNumber}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="invoicing.patienType.patientNumber"
                defaultMessage="Patient No:"
              />
              {` ${inpatientDetails?.inpatientNumber}`}
            </Typography>
          </ConditionalDisplay>
          <Box display="flex" justifyContent="space-between">
            <ConditionalDisplay condition={!!inpatientDetails?.admission}>
              <Typography variant="subtitle2">
                <FormattedMessage
                  id="invoicing.patienType.admission"
                  defaultMessage="From:"
                />
                {inpatientDetails?.admission &&
                  ` ${format(
                    new Date(inpatientDetails.admission),
                    "dd/MM/yyyy",
                  )}`}
              </Typography>
            </ConditionalDisplay>
            <ConditionalDisplay condition={!!inpatientDetails?.discharge}>
              <Typography variant="subtitle2">
                <FormattedMessage
                  id="invoicing.patienType.discharge"
                  defaultMessage="To:"
                />
                {inpatientDetails?.discharge &&
                  ` ${format(
                    new Date(inpatientDetails.discharge),
                    "dd/MM/yyyy",
                  )}`}
              </Typography>
            </ConditionalDisplay>
          </Box>
        </Grid>
      </ConditionalDisplay>
    </Grid>
  );
};
